import React from 'react';
import { SensorData } from '../types';
import { Thermometer, Droplets, Zap, Activity } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';

interface SensorCardProps {
  data: SensorData;
}

const getIcon = (type: SensorData['type']) => {
  switch (type) {
    case 'temperature': return <Thermometer className="w-5 h-5 text-orange-400" />;
    case 'humidity': return <Droplets className="w-5 h-5 text-blue-400" />;
    case 'energy': return <Zap className="w-5 h-5 text-yellow-400" />;
    default: return <Activity className="w-5 h-5 text-cyan-400" />;
  }
};

const SensorCard: React.FC<SensorCardProps> = ({ data }) => {
  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-lg">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-700/50 flex justify-between items-center bg-slate-800/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-900 rounded-lg">
            {getIcon(data.type)}
          </div>
          <div>
            <h3 className="font-medium text-slate-200">{data.name}</h3>
            <p className="text-xs text-slate-500">Updated: {data.lastSeen}</p>
          </div>
        </div>
        <div className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)] animate-pulse"></div>
      </div>

      {/* Values */}
      <div className="p-4 grid grid-cols-1 gap-4 divide-y divide-slate-700/50">
        {data.values.map((val, idx) => (
          <div key={`${data.id}-${idx}`} className="pt-2 first:pt-0">
            <div className="flex justify-between items-end mb-2">
              <span className="text-sm text-slate-400">{val.label}</span>
              <span className="text-2xl font-bold text-slate-100 tracking-tight">
                {val.current}
                <span className="text-base font-normal text-slate-500 ml-1">{val.unit}</span>
              </span>
            </div>
            
            {/* Sparkline Chart */}
            <div className="h-12 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={val.history}>
                  <defs>
                    <linearGradient id={`grad-${idx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#22d3ee" 
                    fillOpacity={1} 
                    fill={`url(#grad-${idx})`} 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SensorCard;